// Pass 2.13 — search state + URL sync + debounced fetch.
//
// Owns:
//   - the live query string the SearchBar binds to
//   - the resolved scope ('conversation' | 'workspace' | 'all')
//   - the in-flight result set (hits, total, has_more, cursor)
//   - the URL ?q=&scope=&conv_id= sync
//   - debounced re-fetches on query change (250ms)
//   - request cancellation on new keystroke (AbortController)
//
// Does NOT own:
//   - the dropdown's open/closed visual state — that's local to the
//     consuming component
//   - the click-to-jump action — exposed separately so consumers
//     can wire it through AppContext + useMessagesPage.jumpToTs
//
// localStorage namespacing: scope persistence under the
// 'slack-archive-viewer:search:*' prefix per spec.

import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";

import {
  ApiError,
  searchAll,
  searchWorkspace,
  type SearchHit,
  type SearchResponse,
  type SearchScope,
} from "./api";

const SCOPE_STORAGE_KEY = "slack-archive-viewer:search:scope";
const DEBOUNCE_MS = 250;

export interface UseSearchOpts {
  /** The currently active workspace, or null. Used as the default
   *  scope target on the workspace endpoint. */
  activeWorkspaceId: string | null;
  /** The currently active conversation, or null. When non-null
   *  the conversation scope option is enabled. */
  activeConversationId: string | null;
  /** Lock the scope to a constant (Home pins "all" — there is no
   *  active workspace there, so a persisted "workspace" scope from a
   *  Browse session must not leak in). When set: setScope is a no-op
   *  and neither localStorage nor the URL is read/written for scope. */
  forceScope?: SearchScope;
}

export interface UseSearchResult {
  query: string;
  setQuery: (q: string) => void;
  scope: SearchScope;
  setScope: (s: SearchScope) => void;
  /** True when the conversation scope option is selectable —
   *  i.e. an activeConversationId is set. */
  conversationScopeAvailable: boolean;
  hits: SearchHit[];
  totalCount: number;
  loading: boolean;
  error: ApiError | null;
  hasMore: boolean;
  /** Trigger fetching one more page of hits past the current
   *  cursor. No-op while loading or hasMore=false. */
  loadMore: () => void;
  /** Clear the query + result set. */
  clear: () => void;
}

function readStoredScope(): SearchScope | null {
  try {
    const v = localStorage.getItem(SCOPE_STORAGE_KEY);
    if (v === "conversation" || v === "workspace" || v === "all") {
      return v;
    }
  } catch {
    // localStorage unavailable (private mode, etc) — ignore.
  }
  return null;
}

function writeStoredScope(scope: SearchScope): void {
  try {
    localStorage.setItem(SCOPE_STORAGE_KEY, scope);
  } catch {
    // ignore
  }
}

function pickDefaultScope(opts: {
  conversationId: string | null;
  workspaceId: string | null;
  stored: SearchScope | null;
}): SearchScope {
  // Prefer URL-supplied scope (handled outside this helper). Then
  // stored preference if compatible with current state. Then a
  // sensible default based on what's selected.
  if (opts.stored) {
    if (opts.stored === "conversation" && !opts.conversationId) {
      return opts.workspaceId ? "workspace" : "all";
    }
    if (opts.stored === "workspace" && !opts.workspaceId) return "all";
    return opts.stored;
  }
  if (opts.conversationId) return "conversation";
  if (opts.workspaceId) return "workspace";
  return "all";
}

export function useSearch(opts: UseSearchOpts): UseSearchResult {
  const [searchParams, setSearchParams] = useSearchParams();
  const urlQ = searchParams.get("q") ?? "";
  const urlScopeRaw = searchParams.get("scope");
  const urlScope: SearchScope | null =
    urlScopeRaw === "conversation"
      || urlScopeRaw === "workspace"
      || urlScopeRaw === "all"
      ? urlScopeRaw
      : null;

  const [query, setQueryState] = useState<string>(urlQ);
  const [scopeState, setScopeState] = useState<SearchScope>(
    () =>
      opts.forceScope ??
      urlScope ??
      pickDefaultScope({
        conversationId: opts.activeConversationId,
        workspaceId: opts.activeWorkspaceId,
        stored: readStoredScope(),
      }),
  );
  const scope = opts.forceScope ?? scopeState;
  const [hits, setHits] = useState<SearchHit[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);
  const [cursor, setCursor] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);

  // Reset scope to default on workspace switch (unless URL has one).
  const lastWorkspaceRef = useRef<string | null>(opts.activeWorkspaceId);
  useEffect(() => {
    if (lastWorkspaceRef.current !== opts.activeWorkspaceId) {
      lastWorkspaceRef.current = opts.activeWorkspaceId;
      if (urlScope === null) {
        setScopeState(
          pickDefaultScope({
            conversationId: opts.activeConversationId,
            workspaceId: opts.activeWorkspaceId,
            stored: readStoredScope(),
          }),
        );
      }
    }
  }, [opts.activeWorkspaceId, opts.activeConversationId, urlScope]);

  // Persist scope choice (per session). We do NOT persist when
  // the URL is the source of truth — URL wins. A forced scope is a
  // page constant, not a user choice — never persisted.
  const scopeForced = opts.forceScope != null;
  useEffect(() => {
    if (!scopeForced) writeStoredScope(scope);
  }, [scope, scopeForced]);

  // URL sync: write q + scope + (when conversation scope) conv_id
  // back into the URL. team_id is workspace-level, owned by
  // AppContext, NOT mirrored here.
  useEffect(() => {
    if (scopeForced) return; // forced scope pages keep their URL clean
    const params = new URLSearchParams(searchParams);
    if (query) {
      params.set("q", query);
    } else {
      params.delete("q");
    }
    params.set("scope", scope);
    if (scope === "conversation" && opts.activeConversationId) {
      params.set("conv_id", opts.activeConversationId);
    } else {
      params.delete("conv_id");
    }
    // Only update when something would actually change to avoid
    // infinite loops with react-router's reactive params object.
    if (params.toString() !== searchParams.toString()) {
      setSearchParams(params, { replace: true });
    }
  }, [query, scope, scopeForced, opts.activeConversationId, searchParams, setSearchParams]);

  // Debounced search execution. AbortController cancels in-flight
  // requests when query/scope/filters change before the previous
  // one resolved.
  useEffect(() => {
    const trimmed = query.trim();
    if (!trimmed) {
      setHits([]);
      setTotalCount(0);
      setHasMore(false);
      setCursor(null);
      setError(null);
      setLoading(false);
      return;
    }
    // Validate scope+context before firing — empty results when
    // the user picked conversation but no conv is active.
    if (scope === "conversation" && !opts.activeConversationId) {
      setHits([]);
      setTotalCount(0);
      setHasMore(false);
      return;
    }
    if (scope === "workspace" && !opts.activeWorkspaceId) {
      setHits([]);
      setTotalCount(0);
      setHasMore(false);
      return;
    }

    const ctrl = new AbortController();
    const timer = window.setTimeout(() => {
      setLoading(true);
      setError(null);
      const fire = async (): Promise<SearchResponse> => {
        if (scope === "all") {
          return searchAll({
            q: trimmed,
            scope: "all",
            signal: ctrl.signal,
          });
        }
        return searchWorkspace({
          q: trimmed,
          scope,
          teamID: opts.activeWorkspaceId ?? undefined,
          convID:
            scope === "conversation"
              ? opts.activeConversationId ?? undefined
              : undefined,
          signal: ctrl.signal,
        });
      };
      fire()
        .then((r) => {
          if (ctrl.signal.aborted) return;
          setHits(r.hits);
          setTotalCount(r.total_count);
          setHasMore(r.has_more);
          setCursor(r.next_cursor);
          setLoading(false);
        })
        .catch((e) => {
          if (ctrl.signal.aborted) return;
          if (e instanceof ApiError) setError(e);
          setLoading(false);
        });
    }, DEBOUNCE_MS);

    return () => {
      window.clearTimeout(timer);
      ctrl.abort();
    };
  }, [
    query,
    scope,
    opts.activeWorkspaceId,
    opts.activeConversationId,
  ]);

  const setQuery = useCallback((q: string) => setQueryState(q), []);
  const setScope = useCallback((s: SearchScope) => setScopeState(s), []);

  const loadMore = useCallback(() => {
    if (loading || !hasMore || cursor === null) return;
    const trimmed = query.trim();
    if (!trimmed) return;
    const ctrl = new AbortController();
    setLoading(true);
    const fire = async (): Promise<SearchResponse> => {
      if (scope === "all") {
        return searchAll({
          q: trimmed,
          scope: "all",
          cursor,
          signal: ctrl.signal,
        });
      }
      return searchWorkspace({
        q: trimmed,
        scope,
        teamID: opts.activeWorkspaceId ?? undefined,
        convID:
          scope === "conversation"
            ? opts.activeConversationId ?? undefined
            : undefined,
        cursor,
        signal: ctrl.signal,
      });
    };
    fire()
      .then((r) => {
        if (ctrl.signal.aborted) return;
        setHits((prev) => [...prev, ...r.hits]);
        setHasMore(r.has_more);
        setCursor(r.next_cursor);
        setLoading(false);
      })
      .catch((e) => {
        if (ctrl.signal.aborted) return;
        if (e instanceof ApiError) setError(e);
        setLoading(false);
      });
  }, [
    loading,
    hasMore,
    cursor,
    query,
    scope,
    opts.activeWorkspaceId,
    opts.activeConversationId,
  ]);

  const clear = useCallback(() => {
    setQueryState("");
    setHits([]);
    setTotalCount(0);
    setHasMore(false);
    setCursor(null);
    setError(null);
  }, []);

  return {
    query,
    setQuery,
    scope,
    setScope,
    conversationScopeAvailable: opts.activeConversationId !== null,
    hits,
    totalCount,
    loading,
    error,
    hasMore,
    loadMore,
    clear,
  };
}
